import { createSlice, current, PayloadAction } from '@reduxjs/toolkit'

import { Workflow } from '../../consts'
import { addAsyncExtraReducers } from '../async-processing/utils'
import { addErrorExtraReducers } from '../error/utils'
import { Measure, UpgradesState } from './types'

const workflow = Workflow.upgrades as keyof typeof Workflow

const initialState: UpgradesState = {
  cloudContext: {
    fromCloud: false,
    scenarioId: null,
    projectId: null,
    sourceUmiFileVersion: null,
  },

  extension: null,
  templates: null,
  measureContext: null,
  templateSelection: null,

  activeFilters: {},

  // Other
  isProcessing: false,

  error: null,
  errors: {},
  runtimeError: null,
}

const upgradesSlice = createSlice({
  name: workflow,
  initialState,
  reducers: {
    loadData: (
      state,
      {
        payload,
      }: PayloadAction<{
        from_cloud: boolean
        scenario_id: number
        project_id: number
        source_umi_file_version: number
        file_extension: string
        names: string[]
        measure_definitions?: UpgradesState['templates']
      }>,
    ) => {
      const {
        from_cloud,
        scenario_id,
        project_id,
        source_umi_file_version,
        file_extension,
        names,
        measure_definitions,
      } = payload
      state.extension = file_extension
      state.templates = {}
      names.forEach((name: string) => {
        if (!state.templates) return
        return (state.templates[name] = {})
      })
      if (from_cloud) {
        state.cloudContext = {
          fromCloud: true,
          scenarioId: scenario_id,
          projectId: project_id,
          sourceUmiFileVersion: source_umi_file_version,
        }
        state.templates = {
          ...(state.templates ?? {}),
          ...(measure_definitions ?? {}),
        }
      }
    },
    addMeasureToTemplate: (
      state,
      { payload }: PayloadAction<{ template: string; measure: Measure }>,
    ) => {
      const { template, measure } = payload
      if (!state.templates) return
      state.templates[template][measure['Measure.Name']] = measure
    },
    loadMeasureDefinitions: (state, { payload }: PayloadAction<UpgradesState['templates']>) => {
      state.templates = {
        ...(state.templates ?? {}),
        ...(payload ?? {}),
      }
    },
    removeMeasureFromTemplate: (
      state,
      { payload }: PayloadAction<{ templateName: string; measureName: string }>,
    ) => {
      delete state.templates?.[payload.templateName]?.[payload.measureName]
    },
    setMeasureContext: (state, { payload }: PayloadAction<UpgradesState['measureContext']>) => {
      state.measureContext = payload
    },
    // TODO: define PayloadAction type
    updateMeasureContext: (state, { payload }) => {
      if (!state.measureContext) return
      state.measureContext.Name = 'User-defined'
      const { config, Value } = payload
      const { 'Prop.Name': PropName, Prop } = config
      const current_preset = state.measureContext.preset_values[PropName] ?? {}
      state.measureContext.preset_values[PropName] = {
        ...current_preset,
        Value,
        Prop,
        'Prop.Prop.Name': PropName,
      }
    },
    setTemplateSelection: (
      state,
      { payload }: PayloadAction<UpgradesState['templateSelection']>,
    ) => {
      state.templateSelection = payload
    },
    setActiveFilter: (
      state,
      { payload }: PayloadAction<{ prop: keyof UpgradesState['activeFilters']; value: string }>,
    ) => {
      const { prop, value } = payload
      if (value) {
        state.activeFilters[prop] = value
      } else if (state.activeFilters[prop]) {
        delete state.activeFilters[prop]
      }
    },
    clearFilters: (state) => {
      state.activeFilters = {}
    },
    clearAllData: (state) => {
      state.activeFilters = {}
      state.cloudContext = {
        fromCloud: false,
        scenarioId: null,
        projectId: null,
        sourceUmiFileVersion: null,
      }
      state.templates = null
      state.templateSelection = null
      state.measureContext = null
      state.extension = null
    },
  },
  extraReducers: (builder) => {
    addErrorExtraReducers(builder, { workflow })
    addAsyncExtraReducers(builder, { workflow, handlers })
  },
})

// Async Action Handlers
const handlers = {
  //     [AsyncTask.processBaseGis]: {
  //         onStart: handleClearBaseGisData,
  //         onSuccess: handleProcessedGis
  //     },
}

// Action creators are generated for each case reducer function
export const {
  loadData,
  loadMeasureDefinitions,
  addMeasureToTemplate,
  removeMeasureFromTemplate,
  setMeasureContext,
  setTemplateSelection,
  setActiveFilter,
  updateMeasureContext,
  clearAllData,
} = upgradesSlice.actions

export default upgradesSlice.reducer
